import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import type { SequenceInputScene } from '@/types/gameSpec';
import { normalize, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

export const SequenceInputSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<SequenceInputScene>) => {
  const [values, setValues] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);

  const add = () => {
    if (!draft.trim() || revealed) return;
    setValues((v) => [...v, draft.trim()]);
    setDraft('');
  };

  const removeAt = (i: number) => {
    if (revealed) return;
    setValues((v) => v.filter((_, idx) => idx !== i));
  };

  const submit = () => {
    if (revealed || values.length === 0) return;
    setRevealed(true);
    const ok =
      values.length === scene.expected.length &&
      values.every(
        (v, i) =>
          normalize(v, scene.case_sensitive) ===
          normalize(scene.expected[i], scene.case_sensitive),
      );
    setTimeout(() => onAnswer(ok, { values }), 800);
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.sequence_input}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            disabled={revealed}
            placeholder="Type next value, then Enter"
          />
          <Button onClick={add} disabled={revealed || !draft.trim()}>
            Add
          </Button>
        </div>

        {values.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {values.map((v, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm bg-muted"
              >
                <span className="text-muted-foreground">{i + 1}.</span> {v}
                {!revealed && (
                  <button
                    onClick={() => removeAt(i)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {!revealed && (
          <Button onClick={submit} disabled={values.length === 0}>
            Submit sequence
          </Button>
        )}
        {revealed && (
          <p className="text-sm text-muted-foreground">
            Expected: {scene.expected.join(', ')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
